import { get, writable } from "svelte/store";
import { LocalStorage } from "$/services/LocalStorage";
import { BackgroundService } from "$/services/BackgroundService";
import { TopicService } from "$/services/TopicService";
import { TokenStore } from "$/stores/TokenStore";
import { TagService } from "$/services/TagService";
import { AnalyticsService } from "$/services/AnalyticsService";
import { getTagsFromComment, addNewTags, getRole, removeUrlParameters, } from "$/utils/Utils";
import { SelectedSnippetsStore } from "../SelectedSnippetsStore";
import { PreferencesStore } from "$/stores/global/PreferencesStore";
import { CacheService, EntityType } from "$/services/CacheService";
import { ErrorStatus, StoreStatus, StoreStatusState, } from "$/models/StoreStatus";
import { derived } from "svelte/store";
import { v4 as uuid } from "uuid";
import { snippetsUtils } from "$/utils/snippetsUtils";
export const snippetArchiveDefaultLimit = 10;
export var TopicAccessLevel;
(function (TopicAccessLevel) {
    TopicAccessLevel[TopicAccessLevel["NO_ACCESS"] = 0] = "NO_ACCESS";
    TopicAccessLevel[TopicAccessLevel["VIEWER"] = 1] = "VIEWER";
    TopicAccessLevel[TopicAccessLevel["COMMENTER"] = 2] = "COMMENTER";
    TopicAccessLevel[TopicAccessLevel["EDITOR"] = 3] = "EDITOR";
    TopicAccessLevel[TopicAccessLevel["MENTION"] = 4] = "MENTION";
    TopicAccessLevel[TopicAccessLevel["OWNER"] = 10] = "OWNER";
})(TopicAccessLevel || (TopicAccessLevel = {}));
var SpecialTopicId;
(function (SpecialTopicId) {
    SpecialTopicId["INBOX"] = "00000000-0000-0000-0000-000000000000";
})(SpecialTopicId || (SpecialTopicId = {}));
const persistedActiveTopicStore = writable({
    topic: null,
    state: new StoreStatusState(),
    snippetArchive: [],
});
export const normalizeUrl = (url) => {
    return url === null || url === void 0 ? void 0 : url.replace(/^(https?:\/\/|\/\/)/, "").split("#")[0].replace(/\/$/, "");
};
// removing notifications count from page name
export const normalizeTopicName = (name) => {
    return name.replace(/\(\d+\)\s?/g, "");
};
function captureEvent(eventName) {
    try {
        AnalyticsService.trackEvent(
        // @ts-ignore
        get(TokenStore).user.id, 
        // @ts-ignore
        get(TokenStore).user.email, eventName);
    }
    catch (error) {
        console.error("CW Analytics failed to capture event", error);
    }
}
const TopicServiceImpl = (chrome === null || chrome === void 0 ? void 0 : chrome.storage) ? BackgroundService : TopicService;
const TagServiceImpl = (chrome === null || chrome === void 0 ? void 0 : chrome.storage) ? BackgroundService : TagService;
const CacheServiceImpl = (chrome === null || chrome === void 0 ? void 0 : chrome.storage) ? BackgroundService : CacheService;
const createActiveTopicStore = () => {
    const { subscribe, set, update } = persistedActiveTopicStore;
    let draggingSnippetId = null;
    let collaborators = [];
    async function loadInboxTopic(tenant) {
        const inboxSnippets = tenant
            ? await TopicServiceImpl.getInboxSnippets()
            : [];
        collaborators = [];
        return {
            id: null,
            name: "Inbox",
            archived: false,
            createdOn: 0,
            tenant,
            tenantId: (tenant === null || tenant === void 0 ? void 0 : tenant.id) || null,
            createdBy: {},
            createdById: "",
            publicRole: 0,
            public: false,
            roles: [],
            snippets: inboxSnippets,
            tags: [],
        };
    }
    async function loadTags(topic) {
        try {
            return await TagServiceImpl.getTenantTags(topic.tenantId, "");
        }
        catch (error) {
            console.error("Error fetching tenant tags:", error);
            return [];
        }
    }
    return {
        subscribe,
        set,
        update,
        clear: () => set({
            topic: null,
            state: new StoreStatusState(),
            snippetArchive: [],
        }),
        updateTopic: async (updatedTopicFields) => {
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            if (topic)
                set(Object.assign(Object.assign({}, store), { topic: Object.assign(Object.assign({}, topic), updatedTopicFields) }));
            await TopicServiceImpl.updateTopic(updatedTopicFields);
            captureEvent("topic-updated");
        },
        updatePublicRole: async (publicRole) => {
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            await TopicServiceImpl.updatePublicRole(publicRole, topic.id);
            set(Object.assign(Object.assign({}, store), { topic: Object.assign(Object.assign({}, topic), { publicRole: publicRole }) }));
            captureEvent("general-role-updated");
            return topic;
        },
        setStatus: (status, topicId) => {
            let state = get(ActiveTopicStore).state;
            state.setStatus(topicId, status);
            update((store) => (Object.assign(Object.assign({}, store), { state })));
        },
        setError: (error, topicId) => {
            let state = get(ActiveTopicStore).state;
            state.setError(topicId, error);
            update((store) => (Object.assign(Object.assign({}, store), { state })));
        },
        setTopic: (topic) => {
            let state = get(ActiveTopicStore).state;
            if (state.shouldApplyUpdate(topic.id)) {
                update((store) => (Object.assign(Object.assign({}, store), { topic })));
            }
        },
        init: async (topicId = null, TokenStore, TenantStore, ClusterStore, openInboxOnError = true) => {
            var _a, _b;
            ActiveTopicStore.clear();
            ActiveTopicStore.setStatus(StoreStatus.INITIAL, topicId);
            let cacheTopicId = topicId !== null && topicId !== void 0 ? topicId : SpecialTopicId.INBOX;
            let topic = await CacheServiceImpl.getEntity(EntityType.TOPIC, cacheTopicId, 
            // @ts-ignore
            (_b = (_a = get(TenantStore)) === null || _a === void 0 ? void 0 : _a.active) === null || _b === void 0 ? void 0 : _b.id);
            if (topic) {
                // set inbox topic id to null
                if (topic.id === SpecialTopicId.INBOX)
                    topic.id = null;
                // populate store from cache
                update((store) => (Object.assign(Object.assign({}, store), { topic })));
                ActiveTopicStore.setStatus(StoreStatus.UPDATING, topicId);
                // load asynchronously without awaiting
                ActiveTopicStore.load(topicId, TokenStore, TenantStore, ClusterStore, openInboxOnError);
                // Return immediately with the cached topic
                return topic;
            }
            else {
                // empty store and set status to loading
                update((store) => (Object.assign(Object.assign({}, store), { topic: null })));
                ActiveTopicStore.setStatus(StoreStatus.LOADING, topicId);
                // load synchronously
                return await ActiveTopicStore.load(topicId, TokenStore, TenantStore, ClusterStore, openInboxOnError);
            }
        },
        load: async (topicId = null, TokenStore, TenantStore, ClusterStore, openInboxOnError = true) => {
            var _a, _b, _c, _d, _e;
            let topic;
            // @ts-ignore
            const tenant = (_a = get(TenantStore)) === null || _a === void 0 ? void 0 : _a.active;
            if (!topicId) {
                topic = await loadInboxTopic(tenant);
            }
            else {
                /**
                 * NOTE: For insatnt load to work we can not rely on server responses. And in some cases
                 *       local cache might have a topic that was removed from the server or the user has
                 *       lost access to. In those cases we're for now loading inbox and we'll need to define
                 *       a way of communicating to user what happend throught a Toast of sorts
                 */
                try {
                    topic = await TopicServiceImpl.getTopic(topicId);
                    // init inbox
                }
                catch (e) {
                    let state = get(ActiveTopicStore).state;
                    if (state.shouldApplyUpdate(topicId)) {
                        ActiveTopicStore.setError(e.error.status, topicId);
                    }
                    if (openInboxOnError)
                        ActiveTopicStore.init(null, TokenStore, TenantStore, ClusterStore);
                    return;
                }
                const actualAccessLevel = getRole(topic, 
                // @ts-ignore
                (_c = (_b = get(TokenStore)) === null || _b === void 0 ? void 0 : _b.user) === null || _c === void 0 ? void 0 : _c.id, 
                // @ts-ignore
                (_d = get(TenantStore)) === null || _d === void 0 ? void 0 : _d.tenants, 
                // @ts-ignore
                (_e = get(ClusterStore)) === null || _e === void 0 ? void 0 : _e.clusters);
                collaborators =
                    actualAccessLevel >= TopicAccessLevel.COMMENTER
                        ? await TopicServiceImpl.getCollaborators(topic.id)
                        : [];
            }
            topic.tags = await loadTags(topic);
            // update store state
            let state = get(ActiveTopicStore).state;
            // update store
            if (state.shouldApplyUpdate(topic.id)) {
                update((store) => (Object.assign(Object.assign({}, store), { topic })));
                ActiveTopicStore.setStatus(StoreStatus.UP_TO_DATE, topicId);
                // just update cache
            }
            else {
                let topicCopy = Object.assign({}, topic);
                if (topicCopy.id === null)
                    topicCopy.id = SpecialTopicId.INBOX;
                await CacheServiceImpl.setEntity(EntityType.TOPIC, topicCopy, tenant.id);
            }
            // TODO: Move to view model
            SelectedSnippetsStore.clear();
            return topic;
        },
        /**
         * NOTE: Not in use yet, but it goes into a direction of laod loading only fron the API to cache
         *       and then having the fuction that loads from cache to the store on user action (button click).
         *       For this to work we need store to be better structured, currently collaborators are totally
         *       off for example, but loading updates from Topic might still be good enough.
         */
        updateFromCache: async () => {
            let store = get(ActiveTopicStore);
            let cachedTopic = await CacheServiceImpl.getEntity(EntityType.TOPIC, store.topic.id, store.topic.tenantId);
            set({
                topic: cachedTopic,
                state: store.state,
                snippetArchive: [],
            });
        },
        loadArchivedSnippets: async (searchTerm, offset, limit = snippetArchiveDefaultLimit) => {
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            let snippetArchive = [];
            if (searchTerm) {
                snippetArchive = await TopicServiceImpl.searchArchivedSnippets(topic.id, searchTerm, limit, offset);
            }
            else {
                snippetArchive = await TopicServiceImpl.getArchivedSnippets(topic.id, limit, offset);
            }
            if (offset > 0) {
                set(Object.assign(Object.assign({}, store), { snippetArchive: [
                        ...store.snippetArchive,
                        ...snippetArchive,
                    ] }));
            }
            else {
                set(Object.assign(Object.assign({}, store), { snippetArchive }));
            }
            return snippetArchive;
        },
        createSnippet: async (snippet, tenantId = null) => {
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            snippet = Object.assign(Object.assign({}, snippet), { id: snippet.id || uuid(), verified: true });
            // @ts-ignore
            if (get(PreferencesStore).addCardsToTheBottom) {
                // set default weight for putting snippet at the bottom
                snippet.weight = -1;
                // increase all other snippet weights by 1000 (by server logic)
                topic.snippets.forEach((s) => (s.weight += 1000));
                // then add our new snippet to the bottom which weight will be 1000
                topic.snippets = [...topic.snippets, snippet];
            }
            else {
                topic.snippets = [snippet, ...topic.snippets];
            }
            set(Object.assign(Object.assign({}, store), { topic, state: store.state }));
            let createdSnippet = await TopicServiceImpl.createSnippet(snippet, topic.id);
            topic = get(ActiveTopicStore).topic;
            topic.snippets = topic.snippets.map((s) => {
                if (s.id === createdSnippet.id) {
                    return Object.assign(Object.assign({}, createdSnippet), { 
                        // maintian comments from the store beacause API does not return them
                        // and when connection is slow user might create a comment before the
                        // snippet is returned
                        comments: s.comments || createdSnippet.comments });
                }
                return s;
            });
            set(Object.assign(Object.assign({}, store), { topic, state: store.state }));
            return createdSnippet;
        },
        updateSnippet: async (snippet) => {
            var _a, _b;
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            // find local snippet
            let snippetIndex = topic.snippets.findIndex((s) => s.id === snippet.id);
            // update local snippet
            let localSnippet = topic.snippets[snippetIndex];
            localSnippet.name = snippet.name || localSnippet.name;
            localSnippet.weight = snippet.weight || localSnippet.weight;
            localSnippet.color = snippet.color || localSnippet.color;
            localSnippet.verified =
                (_b = (_a = snippet.verified) !== null && _a !== void 0 ? _a : localSnippet.verified) !== null && _b !== void 0 ? _b : false;
            topic.snippets[snippetIndex] = localSnippet;
            // if weight is 1 which is minimal weight, increase all other
            // snippet weights by 1000 so the order is maintained
            if (snippet.weight === 1) {
                topic.snippets.forEach((s) => {
                    if (s.id !== snippet.id)
                        s.weight += 1000;
                });
            }
            // if topic is changed remove snippet from old topic
            if ((snippet.topicId && snippet.topicId !== topic.id) ||
                snippet.topicId === null) {
                topic.snippets = topic.snippets.filter((s) => s.id !== snippet.id);
                // if new topic exists in cache, add snippet to it
                let newTopic = await CacheServiceImpl.getEntity(EntityType.TOPIC, snippet.topicId, topic.tenantId);
                if (newTopic) {
                    // set new snippet weight
                    let maxWeight = newTopic.snippets.length > 0
                        ? newTopic.snippets.reduce((max, snippet) => Math.max(max, snippet.weight), 0)
                        : 0;
                    localSnippet.weight = maxWeight + 1000;
                    // save snippet to new topic
                    newTopic.snippets.unshift(localSnippet);
                    await CacheServiceImpl.setEntity(EntityType.TOPIC, newTopic, newTopic.tenantId);
                }
            }
            ActiveTopicStore.setTopic(topic);
            // save to server
            let updatedSnippet = await TopicServiceImpl.updateSnippet(snippet, snippet.topicId);
            captureEvent("snippet-updated");
            return updatedSnippet;
        },
        createSnippetFromURL: async (url) => {
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            // @ts-ignore
            const addToBottom = get(PreferencesStore).addCardsToTheBottom;
            const createdSnippet = await TopicServiceImpl.createSnippet({
                bookmark: true,
                url,
                weight: addToBottom ? -1 : 1000,
            }, topic.id);
            let snippet = Object.assign(Object.assign({}, createdSnippet), { nameSuggestion: createdSnippet.name, comments: [], reactions: [] });
            if (addToBottom) {
                // increase all other snippet weights by 1000 (by server logic)
                topic.snippets.forEach((s) => (s.weight += 1000));
                // then add our new snippet to the bottom which weight will be 1000
                topic.snippets = [...topic.snippets, snippet];
            }
            else {
                topic.snippets = [snippet, ...topic.snippets];
            }
            set(Object.assign(Object.assign({}, store), { topic, state: store.state }));
            return snippet;
        },
        setArchived: (archived) => {
            update((store) => (Object.assign(Object.assign({}, store), { topic: Object.assign(Object.assign({}, store.topic), { archived: archived }) })));
        },
        updateSnippetUrls: async (snippetIds, newUrl) => {
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            const originalUrls = {};
            const snippetsToUpdate = [];
            for (const snippetId of snippetIds) {
                let snippetIndex = topic.snippets.findIndex((s) => s.id === snippetId);
                let localSnippet = Object.assign({}, topic.snippets[snippetIndex]);
                originalUrls[snippetId] = localSnippet.url;
                const originalUrl = new URL(localSnippet.url);
                const newUrlObj = new URL(newUrl);
                newUrlObj.hash = originalUrl.hash;
                localSnippet.url = newUrlObj.toString();
                localSnippet.urlPath = newUrl;
                localSnippet.verified = false;
                topic.snippets[snippetIndex] = localSnippet;
                snippetsToUpdate.push(localSnippet);
            }
            set(Object.assign(Object.assign({}, store), { topic }));
            // Update server
            const updatePromises = snippetsToUpdate.map((snippet) => TopicServiceImpl.updateSnippet({ id: snippet.id, url: newUrl })
                .then(() => ({ status: "fulfilled", id: snippet.id }))
                .catch((error) => ({
                status: "rejected",
                id: snippet.id,
                error,
            })));
            const results = await Promise.all(updatePromises);
            let hasErrors = false;
            results.forEach((result) => {
                if (result.status === "rejected") {
                    hasErrors = true;
                    console.error(`Failed to update snippet ${result.id}:`, result.error);
                    const snippetIndex = topic.snippets.findIndex((s) => s.id === result.id);
                    topic.snippets[snippetIndex].url = originalUrls[result.id];
                    topic.snippets[snippetIndex].urlPath =
                        originalUrls[result.id];
                }
            });
            if (hasErrors) {
                set(Object.assign(Object.assign({}, store), { topic }));
            }
            captureEvent("snippets-url-updated");
            return snippetsToUpdate;
        },
        moveSnippetToInbox: async (snippetId, topicId) => {
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            // find local snippet
            let snippetIndex = topic.snippets.findIndex((s) => s.id == snippetId);
            let localSnippet = topic.snippets[snippetIndex];
            // update local snippet
            localSnippet.topicId = topicId;
            topic.snippets[snippetIndex] = localSnippet;
            // remove snippet from old topic
            topic.snippets = topic.snippets.filter((s) => s.id !== snippetId);
            set(Object.assign(Object.assign({}, store), { topic }));
            // save to server
            await TopicServiceImpl.moveSnippetToInbox(snippetId, topicId);
            return topic;
        },
        deleteSnippet: async (snippetId) => {
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            topic.snippets = topic.snippets.filter((snippet) => {
                return snippet.id != snippetId;
            });
            let snippetArchive = store.snippetArchive.filter((snippet) => {
                return snippet.id != snippetId;
            });
            set(Object.assign(Object.assign({}, store), { topic, snippetArchive }));
            await TopicServiceImpl.deleteSnippet(snippetId, topic.id);
            // NOTE: Optimisitic approach no updates after deletion
        },
        createComment: async (comment, snippetId) => {
            var _a;
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            // add placeholder comment
            comment.id = undefined;
            comment.createdOn = new Date().getTime();
            comment.tags = getTagsFromComment(comment.comment);
            let snippetIndex = topic.snippets.findIndex((s) => s.id == snippetId);
            topic.snippets[snippetIndex].comments.push(comment);
            topic.snippets[snippetIndex].tags = addNewTags(topic.snippets[snippetIndex].tags, comment.tags);
            set(Object.assign(Object.assign({}, store), { topic }));
            let createdComment = await TopicServiceImpl.createComment(comment, snippetId, topic.id);
            createdComment = Object.assign(Object.assign({}, createdComment), { snippetId: snippetId, topicId: topic.id });
            // remove placeholder comment
            topic.snippets[snippetIndex].comments = topic.snippets[snippetIndex].comments.filter((comment) => {
                return comment.id != undefined;
            });
            // add created comment
            topic.snippets[snippetIndex].comments.push(createdComment);
            ActiveTopicStore.addMentionedUserRoles((_a = createdComment.mentionAndCreateMembers) === null || _a === void 0 ? void 0 : _a.filter((mention) => mention.topicRole));
            set(Object.assign(Object.assign({}, store), { topic }));
            captureEvent("comment-created");
            return createdComment;
        },
        updateComment: async (comment, snippetId) => {
            var _a;
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            // add tags from comment
            comment.tags = getTagsFromComment(comment.comment);
            let snippetIndex = topic.snippets.findIndex((s) => s.id == snippetId);
            topic.snippets[snippetIndex].tags = addNewTags(topic.snippets[snippetIndex].tags, comment.tags);
            set(Object.assign(Object.assign({}, store), { topic }));
            let updatedComment = await TopicServiceImpl.updateComment(comment, snippetId, topic.id);
            ActiveTopicStore.addMentionedUserRoles((_a = updatedComment.mentionAndCreateMembers) === null || _a === void 0 ? void 0 : _a.filter((mention) => mention.topicRole));
            captureEvent("comment-updated");
            set(Object.assign(Object.assign({}, store), { topic }));
            return updatedComment;
        },
        addMentionedUserRoles: (newRoles) => {
            if (!newRoles)
                return;
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            if (newRoles.length) {
                topic.roles = [
                    ...topic.roles,
                    ...newRoles.map((mention) => mention.topicRole),
                ];
                //transfering mentioned slack collaborators to topic collaborators
                let createdRolesUserIds = newRoles.map((role) => role.user.id);
                collaborators = collaborators.reduce((members, collaborator) => {
                    members.push(createdRolesUserIds.includes(collaborator.id)
                        ? Object.assign(Object.assign({}, collaborator), { isMember: true }) : collaborator);
                    return members;
                }, []);
                //Adding mentioned email collaborators to topic collaborators
                let collaboratorIds = collaborators.map((el) => el.id);
                let newEmailsMentions = newRoles
                    .filter((mention) => !collaboratorIds.includes(mention.topicRole.user.id))
                    .map((mention) => {
                    return Object.assign(Object.assign({}, mention.topicRole.user), { isMember: true });
                });
                collaborators = [...collaborators, ...newEmailsMentions];
            }
        },
        createTopicRole: async (topicRole) => {
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            // add placeholder if new role
            if (!topic.roles.find((r) => r.user.username == topicRole.user.username)) {
                topicRole.id = undefined;
                topicRole.user.firstName = "Adding new user...";
                topicRole.user.lastName = "";
                topic.roles.push(topicRole);
                set(Object.assign(Object.assign({}, store), { topic }));
            }
            let newRole = await TopicServiceImpl.createTopicRole(topic.id, topicRole);
            //remove old role if user has it
            topic.roles = topic.roles.filter((r) => r.user.id !== newRole.user.id);
            topic.roles.push(newRole);
            ActiveTopicStore.addCollaborator(newRole.user);
            // remove placeholder role
            topic.roles = topic.roles.filter((r) => {
                return r.id != undefined;
            });
            captureEvent("topic-role-created");
            set(Object.assign(Object.assign({}, store), { topic }));
            return topic;
        },
        addVisitorRole: async () => {
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            // @ts-ignore
            let user = get(TokenStore).user;
            let visitorRole = {
                role: TopicAccessLevel.NO_ACCESS,
                user: {
                    id: user.id,
                },
            };
            let newRole = await TopicServiceImpl.createTopicRole(topic.id, visitorRole);
            //add fields needed in view
            newRole.user = Object.assign(Object.assign({}, newRole.user), { firstName: user.firstName, lastName: user.lastName, username: user.email });
            topic.roles.push(newRole);
            captureEvent("visitor-role-created");
            set(Object.assign(Object.assign({}, store), { topic }));
            return topic;
        },
        editTopicRole: async (topicRoleId, topicRoleValue) => {
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            let topicRoleIndex = topic.roles.findIndex((r) => r.id === topicRoleId);
            const updatedRoles = [...topic.roles];
            updatedRoles[topicRoleIndex] = Object.assign(Object.assign({}, updatedRoles[topicRoleIndex]), { role: topicRoleValue });
            const updatedTopic = Object.assign(Object.assign({}, topic), { roles: updatedRoles });
            let newRole = Object.assign(Object.assign({}, updatedRoles[topicRoleIndex]), { role: topicRoleValue, user: {
                    id: updatedRoles[topicRoleIndex].user.id,
                    username: updatedRoles[topicRoleIndex].user.username,
                } });
            await TopicServiceImpl.updateTopicRole(topic.id, newRole);
            set(Object.assign(Object.assign({}, store), { topic: updatedTopic }));
            captureEvent("topic-role-updated");
            return updatedTopic;
        },
        deleteTopicRole: async (topicRoleId) => {
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            topic.roles = topic.roles.filter((r) => r.id !== topicRoleId);
            set(Object.assign(Object.assign({}, store), { topic }));
            await TopicServiceImpl.deleteTopicRole(topic.id, topicRoleId);
            return topic;
        },
        deleteComment: async (comment, snippetId, topicId) => {
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            let snippet = topic.snippets.find((s) => s.id === snippetId);
            snippet.comments = snippet.comments.filter((c) => c.id !== comment.id);
            set(Object.assign(Object.assign({}, store), { topic }));
            await TopicServiceImpl.deleteComment(comment.id, snippetId, topicId);
            // NOTE: Optimisitic approach no updates after deletion
        },
        removeTagFromSnippet: async (tag, snippetId) => {
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            let snippet = topic.snippets.find((s) => s.id === snippetId);
            snippet.tags = snippet.tags.filter((t) => t.id !== tag.id);
            set(Object.assign(Object.assign({}, store), { topic }));
            await TopicServiceImpl.removeTagFromSnippet(tag.id, snippetId, topic.id);
            // NOTE: Optimisitic approach no updates after deletion
        },
        addTagToSnippet: async (tag, snippetId) => {
            var _a;
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            let snippet = topic.snippets.find((s) => s.id === snippetId);
            snippet.tags = (_a = snippet.tags) !== null && _a !== void 0 ? _a : [];
            if (!(snippet === null || snippet === void 0 ? void 0 : snippet.tags.some((t) => t.id === tag.id))) {
                snippet.tags.push(tag);
            }
            set(Object.assign(Object.assign({}, store), { topic }));
            await TopicServiceImpl.addTagToSnippetWithRetry(tag, snippetId, topic.id);
        },
        getTags: async (tenantId) => {
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            try {
                topic.tags = await TagServiceImpl.getTenantTags(tenantId, "");
            }
            catch (error) {
                console.error("Error fetching tenant tags: ", error);
            }
        },
        createTag: async (tag, tenantId) => {
            var _a;
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            // existing user
            if (topic) {
                // add tag to topic
                topic.tags = (_a = topic.tags) !== null && _a !== void 0 ? _a : [];
                topic.tags.push(tag);
                set(Object.assign(Object.assign({}, store), { topic }));
                // set tenant id to topic's
                tenantId = topic.tenantId;
                // new user - ActiveTopicStore is still empty
            }
            else {
                // handle possible errors
                if (!tenantId) {
                    console.error("Tenant Id must not be empty for new users");
                    return;
                }
                let topic = {
                    id: null,
                    name: "",
                    archived: false,
                    createdOn: 0,
                    tenantId: tenantId,
                    createdBy: {},
                    createdById: "",
                    publicRole: 0,
                    public: false,
                    roles: [],
                    snippets: [],
                    tags: [tag],
                };
                set(Object.assign(Object.assign({}, store), { topic }));
            }
            return await TagServiceImpl.createTenantTag(tenantId, tag);
        },
        deleteTag: async (tagId) => {
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            topic.tags = topic.tags.filter((tag) => tag.id !== tagId);
            topic.snippets
                .filter((snippet) => snippet.tags)
                .map((snippet) => {
                if (snippet.tags.some((t) => t.id === tagId)) {
                    snippet.tags = snippet.tags.filter((t) => t.id !== tagId);
                }
            });
            set(Object.assign(Object.assign({}, store), { topic }));
            await TagServiceImpl.deleteTenantTag(topic.tenantId, tagId);
        },
        renameTag: async (tagId, newTagName) => {
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            const tag = topic.tags.find((tag) => tag.id === tagId);
            if (tag) {
                tag.tag = newTagName;
            }
            topic.snippets
                .filter((snippet) => snippet.tags)
                .map((snippet) => {
                snippet.tags = snippet.tags.map((t) => t.id === tagId ? Object.assign(Object.assign({}, t), { tag: newTagName }) : t);
            });
            set(Object.assign(Object.assign({}, store), { topic }));
            await TagServiceImpl.renameTenantTag(topic.tenantId, tagId, newTagName);
        },
        getNumberOfSnippetsWithTag: async (tagId) => {
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            return await TagServiceImpl.getNumberOfSnippetsWithTag(topic.tenantId, tagId);
        },
        getSnippet: async (snippetId) => {
            var _a;
            let store = get(ActiveTopicStore);
            let snippet = (_a = store.topic) === null || _a === void 0 ? void 0 : _a.snippets.find((s) => s.id === snippetId);
            // snippet is likely archived so we need to fetch it
            if (!snippet && snippetId && store.topic) {
                snippet = await TopicServiceImpl.getSnippet(snippetId, store.topic.id);
            }
            return snippet;
        },
        hasSnippet: (snippetId) => {
            var _a, _b;
            let store = get(ActiveTopicStore);
            return (_b = (_a = store.topic) === null || _a === void 0 ? void 0 : _a.snippets.find((s) => s.id === snippetId)) !== null && _b !== void 0 ? _b : false;
        },
        getBookmarkByUrl: (url) => {
            var _a;
            let store = get(ActiveTopicStore);
            const normalizedUrl = removeUrlParameters(url);
            return (_a = store.topic) === null || _a === void 0 ? void 0 : _a.snippets.find((s) => s.bookmark === true &&
                removeUrlParameters(s.url) === normalizedUrl);
        },
        setDraggingSnippetId: (snippetId) => {
            draggingSnippetId = snippetId;
        },
        getOwnerRole: () => {
            var _a;
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            return (_a = topic === null || topic === void 0 ? void 0 : topic.roles) === null || _a === void 0 ? void 0 : _a.find((r) => r.role === TopicAccessLevel.OWNER);
        },
        searchCollaborators: (query) => {
            return collaborators.filter((item) => item.username.toLowerCase().includes(query.toLowerCase()) ||
                (item.firstName &&
                    item.firstName
                        .toLowerCase()
                        .includes(query.toLowerCase())) ||
                (item.lastName &&
                    item.lastName
                        .toLowerCase()
                        .includes(query.toLowerCase())));
        },
        updateTopicUser: (user) => {
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            //setting topic user
            if (topic.createdById === user.id) {
                topic.createdBy = Object.assign(Object.assign({}, topic.createdBy), { firstName: user.firstName, lastName: user.lastName });
                topic.roles = topic.roles.map((role) => role.userId === user.id
                    ? Object.assign(Object.assign({}, role), { user: Object.assign(Object.assign({}, role.user), { firstName: user.firstName, lastName: user.lastName }) }) : role);
            }
            //setting snippets user and snippets comments user
            topic.snippets = topic.snippets.map((snippet) => {
                let tempSnippet = snippet;
                if (snippet.createdById === user.id) {
                    tempSnippet = Object.assign(Object.assign({}, snippet), { createdBy: Object.assign(Object.assign({}, snippet.createdBy), { firstName: user.firstName, lastName: user.lastName }) });
                }
                if (tempSnippet.comments.length) {
                    tempSnippet.comments = tempSnippet.comments.map((comment) => comment.createdById === user.id
                        ? Object.assign(Object.assign({}, comment), { createdBy: Object.assign(Object.assign({}, comment.createdBy), { firstName: user.firstName, lastName: user.lastName }) }) : comment);
                }
                return tempSnippet;
            });
            set(Object.assign(Object.assign({}, store), { topic }));
        },
        changeSnippetPosition: async (oldIndexPosition, newIndexPosition) => {
            if (oldIndexPosition !== newIndexPosition) {
                let store = get(ActiveTopicStore);
                const draggedSnippet = store.topic.snippets[oldIndexPosition];
                if (!draggedSnippet)
                    return;
                store.topic.snippets.splice(oldIndexPosition, 1);
                store.topic.snippets.splice(newIndexPosition, 0, draggedSnippet);
                const beforeSnippet = newIndexPosition === 0
                    ? null
                    : store.topic.snippets[newIndexPosition - 1];
                const afterSnippet = newIndexPosition === store.topic.snippets.length - 1
                    ? null
                    : store.topic.snippets[newIndexPosition + 1];
                let weight = 0;
                // if theres a before snippet, get the difference between the before and after snippet weights and add half of that to the before snippet weight
                if (beforeSnippet && afterSnippet)
                    weight =
                        (beforeSnippet.weight - afterSnippet.weight) / 2 +
                            afterSnippet.weight;
                // if there is no after snippet, divide the before snippet weight by 2
                else if (beforeSnippet && !afterSnippet)
                    weight = beforeSnippet.weight / 2;
                // if there is no before snippet, add 1000 to the after snippet weight
                else if (!beforeSnippet && afterSnippet)
                    weight = afterSnippet.weight + 1000;
                let updatedSnippet = {
                    id: draggedSnippet.id,
                    weight: Math.round(weight),
                };
                await ActiveTopicStore.updateSnippet(updatedSnippet);
            }
        },
        updateSnippetReaction: async (snippetId, topicId, mockReaction) => {
            var _a, _b;
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            // find local snippet
            let snippetIndex = topic.snippets.findIndex((s) => s.id == snippetId);
            let localSnippet = topic.snippets[snippetIndex];
            // update local snippet
            localSnippet.reactions = [
                ...localSnippet.reactions.filter((r) => r.id !== mockReaction.id),
                mockReaction,
            ];
            let localLikeCount = (_b = (_a = localSnippet.reactionsData) === null || _a === void 0 ? void 0 : _a.likeCount) !== null && _b !== void 0 ? _b : 0;
            localSnippet.reactionsData = Object.assign(Object.assign({}, localSnippet.reactionsData), { likeCount: mockReaction.type === 1
                    ? localLikeCount + 1
                    : localLikeCount });
            topic.snippets[snippetIndex] = localSnippet;
            set(store);
            let newReaction = await TopicServiceImpl.updateReaction(snippetId, topicId, mockReaction.type);
            topic.snippets[snippetIndex].reactions = [
                ...topic.snippets[snippetIndex].reactions.filter((r) => r.id !== mockReaction.id),
                Object.assign(Object.assign({}, mockReaction), { id: newReaction.id }),
            ];
            set(store);
        },
        deleteSnippetReaction: async (snippetId, topicId, reactionId) => {
            var _a;
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            // find local snippet
            let snippetIndex = topic.snippets.findIndex((s) => s.id == snippetId);
            let localSnippet = topic.snippets[snippetIndex];
            // update local snippet
            localSnippet.reactions = localSnippet.reactions.filter((r) => r.id !== reactionId);
            localSnippet.reactionsData = Object.assign(Object.assign({}, localSnippet.reactionsData), { likeCount: ((_a = localSnippet.reactionsData) === null || _a === void 0 ? void 0 : _a.likeCount) - 1 });
            topic.snippets[snippetIndex] = localSnippet;
            set(store);
            await TopicServiceImpl.deleteReaction(snippetId, topicId, reactionId);
            return;
        },
        addCollaborator: (user) => {
            let userExists = collaborators.find((collaborator) => collaborator.id === user.id);
            if (userExists === undefined) {
                collaborators.push(user);
            }
            else {
                userExists.isMember = true;
            }
        },
        removeCollaborator: (user) => {
            collaborators = collaborators.filter((collaborator) => collaborator.id !== user.id);
        },
        reloadCollaboratorsAfterClusterChange: (newCluster) => {
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            let topicRoleUsers = topic.roles.map((role) => role.user);
            if (newCluster.clusterRoles) {
                let clusterRoleUsers = newCluster.clusterRoles.map((role) => role.user);
                let topicRoleUserIds = topicRoleUsers.map((user) => user.id);
                collaborators = [
                    ...topicRoleUsers,
                    ...clusterRoleUsers.filter((user) => !topicRoleUserIds.includes(user.id)),
                ];
            }
            else {
                collaborators = topicRoleUsers;
            }
        },
        getSnippetSuggestions: async (content) => {
            let suggestions = null;
            try {
                suggestions =
                    await TopicServiceImpl.getSnippetSuggestions(content);
            }
            catch (error) {
                console.error("Error fetching snippet suggestions:", error);
            }
            return suggestions;
        },
        fetchCollaborators: async () => {
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            collaborators = await TopicServiceImpl.getCollaborators(topic.id);
        },
        isInbox: () => {
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            return (topic === null || topic === void 0 ? void 0 : topic.id) === null;
        },
        checkSnippetVerification: async (snippetId) => {
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            let snippet = await TopicServiceImpl.getSnippet(snippetId, topic.id);
            return snippet.verified;
        },
        archiveSnippet: async (snippet, archived) => {
            let store = get(ActiveTopicStore);
            let topic = store.topic;
            let snippetArchive = store.snippetArchive;
            if (archived) {
                topic.snippets = topic.snippets.filter((el) => el.id !== snippet.id);
                snippetArchive.unshift(Object.assign(Object.assign({}, snippet), { archived }));
            }
            else {
                topic.snippets.push(Object.assign(Object.assign({}, snippet), { archived }));
                topic.snippets = await snippetsUtils.sortByHighestWeight(topic.snippets);
                snippetArchive.splice(snippetArchive.findIndex((el) => el.id === snippet.id), 1);
            }
            set(Object.assign(Object.assign({}, store), { topic, snippetArchive }));
            TopicServiceImpl.updateSnippet({
                id: snippet.id,
                archived: archived,
            });
        },
    };
};
export const ActiveTopicStore = createActiveTopicStore();
export const ActiveTopic = derived(ActiveTopicStore, ($store) => $store.topic);
export const ActiveTopicArchive = derived(ActiveTopicStore, ($store) => $store.snippetArchive);
ActiveTopic.subscribe(async (topic) => {
    if (topic) {
        await LocalStorage.set("activeTopicId", topic.id);
        let topicCopy = Object.assign({}, topic);
        if (topic.id === null)
            topicCopy.id = SpecialTopicId.INBOX;
        await CacheServiceImpl.setEntity(EntityType.TOPIC, topicCopy, topic.tenantId);
    }
});
